import { defineStore } from 'pinia'
import { useCartStore } from '~/stores/cart'
import { useUserStore } from '~/stores/user'
import { useShippingBenefitStore } from '~/stores/shippingBenefit'
import { applyShippingBenefit } from '~/lib/shippingBenefit'
import deliveryMethodGroups from '~/config/delivery-method-groups.json'
import deliverMethod from '~/config/delivery-methods.json'
import paymentMethodGroups from '~/config/payment-method-groups.json'
import type { CartAddress, CountryCode } from '~/types/cart'
import type { CheckoutState, CustomerInfo, DeliveryMethod, DeliveryMethodGroup, DeliveryMethodGroupWithMethods, DeliveryMethodServicePoint, DeliveryMethodServicePointQueryResult, DeliveryMethodServicePointResultData, PartPaymentPlanCampaign, PaymentMethod, PaymentMethodGroup, PaymentMethodGroupWithOptions, ServiceProvider } from '~/types/checkout'
import type { UserAddress } from '~/types/user'
import type { PaymentCart } from '~/types/paymentGateway'

const getClearAddress = (): CartAddress => ({
  firstname: '',
  lastname: '',
  phone: '',
  street1: '',
  postcode: '',
  city: '',
  countryCode: 'FI',
  company: null,
  vatNumber: null
})

export const useCheckoutStore = defineStore('checkout', {
  state: (): CheckoutState => ({
    selectedDeliveryMethodServicePoint: null as DeliveryMethodServicePoint | null,
    isFetchingDeliveryMethodServicePoints: false,
    isFetchingDeliveryMethods: false,
    deliveryMethodServicePointFetchErrors: [] as DeliveryMethodServicePointQueryResult[],
    deliveryMethodServicePoints: [] as DeliveryMethodServicePointResultData[],
    allDeliveryMethodGroups: deliveryMethodGroups as unknown as DeliveryMethodGroup[],
    allDeliveryMethods: deliverMethod as unknown as DeliveryMethod[],
    selectedDeliveryMethod: null,
    allPaymentMethodGroups: paymentMethodGroups as PaymentMethodGroup[],
    availablePaymentMethods: [],
    selectedPaymentMethod: null,
    activeCheckoutStepIndex: 0,
    isFetchingPaymentMethods: false,
    hasPaymentMethodFetchFailed: false,
    paymentMethodsFetchedWithSum: null,
    hasSeparateShippingAddress: false,
    isBusinessCustomer: false,
    saveAsDefault: false,
    partPaymentPlan: null,
    user: {
      firstname: '',
      lastname: '',
      phone: '',
      email: ''
    },
    addresses: {
      billing: getClearAddress(),
      shipping: getClearAddress()
    },
    comment: '',
    partialDelivery: false
  }),
  getters: {
    getSelectedDeliveryMethodServicePoint: (state): DeliveryMethodServicePoint | null => state.selectedDeliveryMethodServicePoint,
    getIsFetchingDeliveryMethodServicePoints: (state): boolean => state.isFetchingDeliveryMethodServicePoints,
    getSelectedDeliveryMethod: state => state.selectedDeliveryMethod,
    getDeliveryMethodServicePointsByServiceProvider: state => (serviceProvider: ServiceProvider, parcelLockersOnly: boolean = false): DeliveryMethodServicePointResultData | null => {
      return state.deliveryMethodServicePoints?.find(dmsp => dmsp.serviceProvider === serviceProvider && dmsp.parcelLockersOnly === parcelLockersOnly) || null
    },
    getDeliveryMethodServicePointFetchErrorByServiceProvider: (state) => {
      return (serviceProvider: ServiceProvider): DeliveryMethodServicePointQueryResult | null => {
        return (
          state.deliveryMethodServicePointFetchErrors?.find(
            dmsp => dmsp.serviceProvider === serviceProvider
          ) || null
        )
      }
    },
    getDeliveryMethodsForCart(): DeliveryMethod[] {
      const { getCartHighestMinimumShippingPrices } = useShipping()
      const deliveryMethodsFromCartItems = getCartHighestMinimumShippingPrices(useCartStore().items)
      // A reached shipping benefit (toimitusetu) makes its delivery methods free
      return applyShippingBenefit(deliveryMethodsFromCartItems, useShippingBenefitStore().freeDeliveryMethodIds)
        .filter((dm): dm is DeliveryMethod => dm !== undefined)
        .sort((a: DeliveryMethod, b: DeliveryMethod) => a.price > b.price ? 1 : -1)
    },
    extendDeliveryMethodGroup() {
      return (group: DeliveryMethodGroup): DeliveryMethodGroupWithMethods | null => {
        let deliveryMethods = this.getDeliveryMethodsForCart.filter((dm: DeliveryMethod) => group.id === dm.group)
        if (this.isBusinessCustomer) {
          deliveryMethods = [...deliveryMethods, ...this.allDeliveryMethods.filter(dm => dm.group === 'Business') as DeliveryMethod[]]
        }
        if (!deliveryMethods.length) return null
        const lowestPrice = deliveryMethods.reduce((prev: DeliveryMethod, curr: DeliveryMethod) => prev.price < curr.price ? prev : curr).price
        const highestPrice = deliveryMethods.reduce((prev: DeliveryMethod, curr: DeliveryMethod) => prev.price > curr.price ? prev : curr).price
        return (
          {
            ...group,
            deliveryMethods,
            lowestPrice,
            highestPrice
          })
      }
    },
    getGroupedDeliveryMethodsForCart(state): DeliveryMethodGroupWithMethods[] {
      return state.allDeliveryMethodGroups.map((dmg: DeliveryMethodGroup) => this.extendDeliveryMethodGroup(dmg)).filter((group): group is DeliveryMethodGroupWithMethods => group !== null)
    },
    grandTotalInclTax: (state): number => {
      const cartStore = useCartStore()
      const cartTotal = cartStore.totalInclTax || 0
      const deliveryFee = state.selectedDeliveryMethod?.price || 0
      const couponDiscount = cartStore.campaignDiscountAmount || 0
      return Math.max(0, cartTotal + deliveryFee - couponDiscount)
    },
    grandTotalExclTax: (state): number => {
      const cartStore = useCartStore()
      const cartTotal = cartStore.totalExclTax || 0
      const deliveryFee = state.selectedDeliveryMethod?.price || 0
      const couponDiscount = cartStore.campaignDiscountAmount || 0
      return Math.max(0, cartTotal + deliveryFee - couponDiscount)
    },
    availablePaymentMethodGroups: (state): PaymentMethodGroup[] => state.allPaymentMethodGroups.filter((pmg: PaymentMethodGroup) => pmg.availableFor.includes(state.isBusinessCustomer ? 'business' : 'private')),
    getGroupOfSelectedDeliveryMethod: (state): DeliveryMethodGroup | null => state.allDeliveryMethodGroups?.find(dmg => dmg.id === state.selectedDeliveryMethod?.group) || null,
    getGroupedPaymentMethods(): PaymentMethodGroupWithOptions[] {
      return this.availablePaymentMethodGroups
        .map((pmg: PaymentMethodGroup) => (
          {
            ...pmg,
            paymentMethods: this.availablePaymentMethods.slice().filter((pm: PaymentMethod) => pmg.paymentMethodIds.includes(pm.code)).sort((a: PaymentMethod, b: PaymentMethod) => a.displayName.localeCompare(b.displayName, 'fi'))
          }))
        .filter((pmg: PaymentMethodGroupWithOptions) => pmg.paymentMethods.length)
    },
    getGroupOfSelectedPaymentMethod: (state): PaymentMethodGroup | null => state.allPaymentMethodGroups.find(pmg => pmg.paymentMethodIds.includes(state.selectedPaymentMethod?.code || '')) || null,
    getCampaignPartPaymentPlans: (state): PartPaymentPlanCampaign[] => state.partPaymentPlan?.campaigns?.filter((ppp: PartPaymentPlanCampaign) => ppp.NrOfInterestFreeMonths === ppp.ContractLengthInMonths && ppp.ContractLengthInMonths > 3) || [],
    getPartPaymentPlansSortedByContractLength: state => state.partPaymentPlan?.campaigns?.slice().sort((a: PartPaymentPlanCampaign, b: PartPaymentPlanCampaign) => a.ContractLengthInMonths - b.ContractLengthInMonths) || [],
    getLongestCampaignPartPaymentPlan(): PartPaymentPlanCampaign | null {
      return this.getCampaignPartPaymentPlans.length > 0 ? this.getCampaignPartPaymentPlans.reduce((currentHighest: PartPaymentPlanCampaign, partPayment: PartPaymentPlanCampaign) => currentHighest.ContractLengthInMonths > partPayment.ContractLengthInMonths ? currentHighest : partPayment) : null
    },
    getPartPaymentPlanSortedByMonthlyAnnuity: state => state.partPaymentPlan?.campaigns?.slice().sort((a: PartPaymentPlanCampaign, b: PartPaymentPlanCampaign) => a.MonthlyAnnuity - b.MonthlyAnnuity) || [],
    getCheapestMonthlyAnnuityPaymentPlan(): PartPaymentPlanCampaign | null {
      return this.getPartPaymentPlanSortedByMonthlyAnnuity.length ? this.getPartPaymentPlanSortedByMonthlyAnnuity[0] : null
    }
  },
  actions: {
    async payOrder(): Promise<any> {
      const userStore = useUserStore()
      const cartStore = useCartStore()
      const uid = userStore.loggedIn && userStore.user ? userStore.user.uid : null
      const email = userStore.loggedIn && userStore.user ? userStore.user.email : null
      const items: any[] = cartStore.items
      const selectedShippingMethod = { ...this.selectedDeliveryMethod! }
      const campaign = cartStore.campaign
      const giftCard = cartStore.giftCard

      // Remove company and vatNumber from addresses if not business customer
      if (!this.isBusinessCustomer) {
        this.addresses.billing.company = null
        this.addresses.shipping.company = null
        this.addresses.billing.vatNumber = null
        this.addresses.shipping.vatNumber = null
      }

      const cart: PaymentCart = {
        items,
        selectedShippingMethod,
        uid,
        email,
        comment: this.comment,
        giftCard,
        selectedPaymentMethod: this.selectedPaymentMethod!,
        selectedPaymentProvider: this.selectedPaymentMethod!.code === 'KLARNA' ? 'klarna' : 'svea',
        user: this.user,
        addresses: {
          billing: this.addresses.billing,
          shipping: this.addresses.shipping
        },
        partialDelivery: this.partialDelivery,
        isBusinessCustomer: this.isBusinessCustomer
      }
      if (campaign) {
        cart.campaign = {
          code: campaign.code
        }
      }

      return $fetch(useRuntimeConfig().public.FUNCTIONS_API_URL + '/tkapi-paymentGateway-payOrderV2', {
        method: 'POST',
        body: { cart },
        headers: await useAuthHeaders()
      })
        .then((response: any) => {
          return response
        })
        .catch((error) => {
          console.error(error)
          return false
        })
    },
    selectServicePoint(payload: DeliveryMethodServicePoint | null) {
      this.selectedDeliveryMethodServicePoint = payload
    },
    selectDeliveryMethod(method: DeliveryMethod) {
      this.selectedDeliveryMethod = method
    },
    setIsFetchingDeliveryMethodServicePoints(payload: boolean) {
      this.isFetchingDeliveryMethodServicePoints = payload
    },
    setSelectedDeliveryMethod(payload: DeliveryMethod | null) {
      this.selectedDeliveryMethod = payload
    },
    setIsFetchingDeliveryMethods(payload: boolean) {
      this.isFetchingDeliveryMethods = payload
    },

    saveCustomerInfo(payload: CustomerInfo) {
      this.addresses = payload.addresses
      this.user = payload.user
      this.isBusinessCustomer = payload.isBusinessCustomer
      this.hasSeparateShippingAddress = payload.hasSeparateShippingAddress
      if (!payload.hasSeparateShippingAddress) {
        this.addresses.shipping = { ...this.addresses.billing }
      }
    },
    prefillUserAddresses() {
      const userStore = useUserStore()
      if (userStore.loggedIn) {
        const defaultBillingAddress = userStore.details?.addresses?.find((address: UserAddress) => address.defaultBilling)
        if (defaultBillingAddress && (JSON.stringify(this.addresses?.billing) === JSON.stringify(getClearAddress()))) {
          const mappedBillingAddress: CartAddress = {
            firstname: userStore.details.firstname,
            lastname: userStore.details.lastname,
            phone: defaultBillingAddress.phone || '',
            street1: defaultBillingAddress.street,
            postcode: defaultBillingAddress.postcode,
            city: defaultBillingAddress.city,
            countryCode: defaultBillingAddress.countryCode as CountryCode,
            company: defaultBillingAddress.company || null,
            vatNumber: defaultBillingAddress.vatNumber || null
          }
          const defaultShippingAddress = userStore.details?.addresses?.find((address: UserAddress) => address.defaultShipping)
          if (defaultShippingAddress && (JSON.stringify(this.addresses?.shipping) === JSON.stringify(getClearAddress()))) {
            const mappedShippingAddress: CartAddress = {
              firstname: userStore.details.firstname,
              lastname: userStore.details.lastname,
              phone: defaultShippingAddress.phone || '',
              street1: defaultShippingAddress.street,
              postcode: defaultShippingAddress.postcode,
              city: defaultShippingAddress.city,
              countryCode: defaultShippingAddress.countryCode as CountryCode,
              company: defaultShippingAddress.company || null,
              vatNumber: defaultShippingAddress.vatNumber || null
            }
            this.addresses.shipping = mappedShippingAddress
          }
          this.addresses.billing = mappedBillingAddress
          this.updateUserData({ key: 'email', value: userStore.user?.email || '' })
        }
      }
    },
    updateUserData(payload: { key: keyof CustomerInfo['user'], value: any }) {
      this.user[payload.key] = payload.value
    },
    validateSelectedDeliveryMethod() {
      if (this.selectedDeliveryMethod && !this.getDeliveryMethodsForCart.includes(this.selectedDeliveryMethod)) {
        this.selectedDeliveryMethod = null
      }
    },
    async fetchPaymentMethods() {
      this.hasPaymentMethodFetchFailed = false
      const grandTotal = this.grandTotalInclTax
      let availablePaymentMethods: PaymentMethod[] = []
      if (this.paymentMethodsFetchedWithSum === grandTotal) {
        return
      }
      else if (grandTotal === 0) {
        availablePaymentMethods.push({
          displayName: 'Lahjakortti',
          code: 'zeroPayment',
          imageUrl: ''
        })
      }
      else {
        this.isFetchingPaymentMethods = true
        // A failed fetch (e.g. Svea 503) must end as an empty list: the old { data: [] } fallback could not be spread,
        // the fetch threw, and the payment step stayed in its loading state for good (QA 19.10.2026)
        const sveaPaymentMethods: PaymentMethod[] = await $fetch<PaymentMethod[]>(useRuntimeConfig().public.FUNCTIONS_API_URL + '/tkapi-paymentGateway-svea-getPaymentMethodsV2?amount=' + grandTotal.toFixed(2))
          .then(response => Array.isArray(response) ? response : Promise.reject(new Error('Unexpected payment methods response')))
          .catch((error: unknown) => {
            console.error('Payment methods fetch failed', error)
            this.hasPaymentMethodFetchFailed = true
            return []
          })

        if (sveaPaymentMethods.length) {
          this.paymentMethodsFetchedWithSum = grandTotal
        }
        availablePaymentMethods = [
          ...sveaPaymentMethods,
          {
            displayName: 'Klarna',
            code: 'KLARNA',
            imageUrl: '/assets/images/checkout/payment-icons/klarna.png'
          }
        ]
      }
      this.availablePaymentMethods = availablePaymentMethods
      this.isFetchingPaymentMethods = false
    },
    async fetchPartPaymentPlanForAmount(payload: number) {
      this.partPaymentPlan = null
      const partPaymentPlan: any = await $fetch(useRuntimeConfig().public.FUNCTIONS_API_URL + '/tkapi-paymentGateway-svea-fetchPartPaymentPlanV2?amount=' + payload)
      this.partPaymentPlan = partPaymentPlan
    },

    async fetchDeliveryMethodServicePointsByPostcode(postcode: any) {
      this.isFetchingDeliveryMethodServicePoints = true
      const config = useRuntimeConfig()
      try {
        const apiUrl = `${config.public.FUNCTIONS_API_URL}/tkapi-shipping-getShippingMethodServicePointsByPostcodeV2`
        const response = await $fetch<DeliveryMethodServicePointQueryResult[]>(apiUrl, { method: 'POST', body: { postcode } })

        this.deliveryMethodServicePoints = response
          .filter((res: any) => res.statusCode === 200)
          .map((res: any) => res.result)
      }
      catch (error) {
        console.error('Error fetching pickup points:', error)
        return null
      }
      finally {
        this.isFetchingDeliveryMethodServicePoints = false
      }
    },
    validateSelectedPaymentMethod() {
      if (this.selectedPaymentMethod && !this.availablePaymentMethods.some((pm: PaymentMethod) => pm.code === this.selectedPaymentMethod?.code)) {
        this.selectedPaymentMethod = null
      }
    },
    selectOnlyPaymentMethod() {
      if (this.availablePaymentMethods.length === 1) {
        this.selectedPaymentMethod = this.availablePaymentMethods[0]
      }
    }
  }
})
